import {
  Component,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FilterBarService } from '@modules/filter/services/filter-bar.service';
import { Router } from '@angular/router';

import { Analysis, AnalysisFilter, AnalysisService } from 'src/app/api/vat';

import { InternalFilterService } from '@modules/filter/services/internal-filter.service';
import { Column } from 'src/app/global-models';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import config from 'src/assets/config.json';
import { LoadingTemplateFactoryService } from '@modules/commons/services/loading-template-factory.service';
import { LabelGlossaryService } from '@modules/label-glossary/services/label-glossary.service';
import { AnalyzeHelperService } from '@modules/analyzes/services/analyze-helper.service';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-analyze',
  templateUrl: './analyze.component.html',
  styleUrls: ['./analyze.component.scss'],
})
export class AnalyzeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() columns: Column[];
  @Output() columnsNames = new EventEmitter<string[]>();

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  filterBarState: boolean = false;
  columnKeys: string[] = [];
  analysisFilter: AnalysisFilter;
  globalAnalysisId: number = config.GlobalAnalysisId;
  subscriptions: Subscription[] = [];
  dataSource: MatTableDataSource<Analysis> = new MatTableDataSource<Analysis>();
  groups: AnalysisGroup[] = [];
  alleAnalysis: Analysis | null = null;

  @Input() view;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  constructor(
    private filterBarService: FilterBarService,
    private router: Router,
    private analysisService: AnalysisService,
    private internalFilterService: InternalFilterService,
    private loadingTemplateFactoryService: LoadingTemplateFactoryService,
    private labelGlossaryService: LabelGlossaryService,
    private analyzeHelperService: AnalyzeHelperService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.labelGlossaryService.analyzeColumnItemDefinition$.subscribe(
        (analyzeColumnItemDefinition: Column[]) => {
          this.columnKeys = analyzeColumnItemDefinition.map((s) => s.key);
        }
      )
    );
    this.load();
  }

  private load() {
    this.subscriptions.push(
      this.internalFilterService.analysisFilter
        .pipe(
          filter((af) => af.periods !== null),
          debounceTime(2000)
        )
        .subscribe(
          (analysisFilter: AnalysisFilter) => {
            this.dataSource.data =
              this.loadingTemplateFactoryService.createCardAnalyzesTemplate(
                new Array(30)
              );
            this.analysisFilter = analysisFilter;
            this.subscriptions.push(
              this.analysisService
                .getByFilter(analysisFilter)
                .subscribe((analysis: Analysis[]) => {
                  this.dataSource.data = analysis;
                  this.buildGroups(analysis);
                })
            );
          },
          (error: Error) => {}
        )
    );

    this.subscriptions.push(
      this.filterBarService.filterBarState.subscribe((value) => {
        this.filterBarState = value;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.columnsNames.emit(this.columns.map((s) => s.name));
  }

  navigateToPosting(row: Analysis) {
    this.analyzeHelperService.addAnalyzeToLatestLooked(row.analysisId);
    this.internalFilterService.updatePostingFilterFromAnalysisFilter(
      row.analysisId
    );
    this.router.navigate(['posteringer/', row.analysisId]);
  }

  toggleGroup(group: AnalysisGroup): void {
    group.expanded = !group.expanded;
  }

  private buildGroups(analyses: Analysis[]): void {
    const groupMap = new Map<string, Analysis[]>();
    const ungrouped: Analysis[] = [];
    this.alleAnalysis = null;

    for (const analysis of analyses) {
      const name: string = analysis.title ?? '';

      if (name === 'Alle fakturaer og posteringer') {
        this.alleAnalysis = analysis;
        continue;
      }

      const parent = ANALYSIS_GROUP_MAP[name];
      if (parent) {
        if (!groupMap.has(parent)) groupMap.set(parent, []);
        groupMap.get(parent)!.push(analysis);
      } else {
        ungrouped.push(analysis);
      }
    }

    if (ungrouped.length > 0) {
      const existing = groupMap.get('Øvrige analyser') ?? [];
      groupMap.set('Øvrige analyser', [...existing, ...ungrouped]);
    }

    this.groups = Array.from(groupMap.entries()).map(([parent, analyses]) => ({
      parent,
      analyses,
      expanded: false,
    }));
  }
}

export interface AnalysisGroup {
  parent: string;
  analyses: Analysis[];
  expanded: boolean;
}

const ANALYSIS_GROUP_MAP: Record<string, string> = {
  'Øreafrunding': 'Øvrige analyser',
  'Indtægter – poster uden leverandør og bogført uden moms': 'Øvrige analyser',
  'Udgifter til eksterne på art 9': 'Analyser i relation til art 1 Løn',
  'Intern fakturering - art 9': 'Øvrige analyser',
  'Intern fakturering - øvrige arter': 'Øvrige analyser',
  'Ældreboliger': 'Analyser i relation til momsrefusionsbekendtgørelsen - §1, stk. 4',
  'Gavekort': 'Øvrige analyser',
  'Rykker i fakturavarelinje': 'Analyser i relation til en faktura med/uden moms',
  'Interne lønudgifter - art 1': 'Analyser i relation til art 1 Løn',
  'Udgifter til eksterne på art 1 løn': 'Analyser i relation til art 1 Løn',
  'Beregnede omkostninger – art 0': 'Øvrige analyser',
  'Udgifter på indtægtsarter': 'Øvrige analyser',
  'Husleje – takstbetalinger': 'Analyser i relation til art 7.1 Huslejeindtægter',
  'Husleje – opsamling': 'Analyser i relation til art 7.1 Huslejeindtægter',
  'Forskel på bogført beløb og faktura beløb': 'Analyser i relation til en faktura med/uden moms',
  'Busdrift': 'Analyser i relation til momsrefusionsbekendtgørelsen - §1, stk. 4',
  'Trafikselskaber': 'Øvrige analyser',
  'Konteret i henhold til positivlisten': 'Analyser i relation til positivlisten',
  'Udgifter til apoteker': 'Øvrige analyser',
  'Faktura med moms svarer til bogført moms': 'Analyser i relation til en faktura med/uden moms',
  'Faktura med moms og bogført med moms': 'Analyser i relation til en faktura med/uden moms',
  'Faktura med moms og bogført uden moms – positivlisten': 'Analyser i relation til en faktura med/uden moms',
  'Faktura med moms og bogført uden moms': 'Analyser i relation til en faktura med/uden moms',
  'Tema positivlisten – HK 0': 'Analyser i relation til positivlisten',
  'Tema positivlisten – HK 3': 'Analyser i relation til positivlisten',
  'Tema positivlisten – HK 4': 'Analyser i relation til positivlisten',
  'Tema positivlisten – HK 5': 'Analyser i relation til positivlisten',
  'Tema positivlisten – HK 6': 'Analyser i relation til positivlisten',
  'Moms i fakturavarelinje': 'Analyser i relation til en faktura med/uden moms',
  'Overførsler til personer på art 5.2': 'Øvrige analyser',
  'Tema positivlisten – tilskud og støtte': 'Analyser i relation til positivlisten',
  'Øvrige tilskud': 'Øvrige analyser',
  'Fondsmoms': 'Øvrige analyser',
  'Erstatninger': 'Øvrige analyser',
  'Faktura uden moms og bogført uden moms': 'Analyser i relation til en faktura med/uden moms',
  'Faktura uden moms og bogført med moms': 'Analyser i relation til en faktura med/uden moms',
  'Opsamling - omfattet af positivlisten - ukendt': 'Analyser – Opsamling',
  'Opsamling - omfattet af positivlisten - øvrige': 'Analyser – Opsamling',
  'Opsamling - poster med leverandørnavn og bogført uden moms': 'Analyser – Opsamling',
  'Opsamling - poster med leverandørnavn og bogført med moms': 'Analyser – Opsamling',
  'Opsamling - poster uden leverandørnavn og bogført uden moms': 'Analyser – Opsamling',
  'Indtægter – poster uden leverandørnavn og bogført med moms': 'Øvrige analyser',
  'Opsamling - poster uden leverandørnavn og bogført med moms': 'Analyser – Opsamling',
};

